import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { Smartphone, Clock, Sparkles } from 'lucide-react';
import WearableConnections from '@/components/health-hub/WearableConnections';
import UnifiedHealthTimeline from '@/components/health-hub/UnifiedHealthTimeline';
import AIHealthSummary from '@/components/health-hub/AIHealthSummary';

type TabType = 'devices' | 'timeline' | 'summary';

interface Tab {
  id: TabType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  component: React.ComponentType;
}

const tabs: Tab[] = [
  {
    id: 'devices',
    label: 'Connected Devices',
    icon: Smartphone,
    component: WearableConnections,
  },
  {
    id: 'timeline',
    label: 'Health Timeline',
    icon: Clock,
    component: UnifiedHealthTimeline,
  },
  {
    id: 'summary',
    label: 'AI Summary',
    icon: Sparkles,
    component: AIHealthSummary,
  },
];

const HealthHub: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('devices');

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || WearableConnections;

  return (
    <div className="px-4 sm:px-6 py-6 max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Health Hub
        </h1>
        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
          Your unified health data center
        </p>
      </div>

      {/* Tab Bar */}
      <div className="mb-6 overflow-x-auto">
        <div className="inline-flex gap-2 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg min-w-full sm:min-w-0">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-2.5 rounded-md transition-all duration-200 whitespace-nowrap flex-1 sm:flex-none justify-center',
                  isActive
                    ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-sm font-medium'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                )}
              >
                <Icon className="w-4 h-4" />
                <span className="text-sm">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <div className="animate-fadeIn">
        <ActiveComponent />
      </div>
    </div>
  );
};

export default HealthHub;
